import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import { Layout, theme, Modal, Input, Select, message, Button } from "antd";
import Sidebar from "./components/common/Sidebar";
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";
import MobileBottomNav from "./components/common/MobileBottomNav";

import SitePage from "./pages/SitePage";
import DashboardVMSPage from "./pages/vms/DashboardPage";
import ProgramVMSPage from "./pages/vms/ProgramPage";
import ProgramPatrolPage from "./pages/patrol/ProgramPage";
import CyclePatrolPage from "./pages/patrol/CyclePatrolPage";
import SubContractVMSPage from "./pages/vms/SubContractPage";
import DeviceVMSPage from "./pages/DevicePage";
import DeviceNotiPage from "./pages/DeviceNotiPage";
import NotifyPage from "./pages/NotifyPage";
import ProfilePage from "./pages/ProfilePage";

import IndexDocsPage from "./pages/docs/IndexPage";
import EmployeePage from "./pages/docs/EmployeePage";
import StockPage from "./pages/docs/StockPage";
import ContractPage from "./pages/docs/ContractPage";
import SOWPage from "./pages/docs/SOWPage";
import TrainingPage from "./pages/docs/TrainingPage";

import DashboardPage from "./pages/dashboard/Index";
import DashboardPatrolPage from "./pages/patrol/DashboardPage";
import DashboardTimeAttendancePage from "./pages/timeattendance/DashboardPage";
import DashboardDailyPage from "./pages/daily/DashboardPage";
import DashboardCentralizePage from "./pages/centralize/DashboardPage";
import DashboardIOTPage from "./pages/iot/DashboardPage";

import { useSelector, useDispatch } from "react-redux";

import { getAccount, getAccountLimit } from "./reducers/accountSlice";
import { getAccountService, getAccountLimitService } from "./services/account";
import { setSiteAccount } from "./reducers/siteSlice";
import { getSiteService } from "./services/site";
import { signInWithEmail, signInWithUsername } from "./services/auth";

import { getEventService, updateEventService } from "./services/event";

import "./App.css";

const modalStyles = {
  mask: {
    backdropFilter: "blur(10px)",
    background: "rgba(0, 0, 0, 0.4)",
  },
  content: {
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
  },
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const accountType = localStorage.getItem("accountType");
  
  // If accountType is "0" (หน่วยงาน), redirect to home page
  if (accountType === "0") {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const App = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Auto-collapse sidebar on mobile
      if (mobile) {
        setCollapsed(true);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Handle mobile sidebar close event
  useEffect(() => {
    const handleCloseSidebar = () => {
      setCollapsed(true);
    };

    window.addEventListener('closeSidebar', handleCloseSidebar);
    return () => window.removeEventListener('closeSidebar', handleCloseSidebar);
  }, []);

  const selectedSystem = useSelector((state) => state.system.system);
  const account = useSelector((state) => state.account.account);
  const accountLimit = useSelector((state) => state.account.accountLimit);
  const selectedSite = useSelector((state) => state.site.site);
  const [accountType, setAccountType] = useState("0");

  const [messageApi, contextHolder] = message.useMessage();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [isAuth, setIsAuth] = useState(
    localStorage.getItem("account") !== null
  );

  const [event, setEvent] = useState({});

  const dispatch = useDispatch();

  const systemName = {
    1: "vms",
    2: "patrol",
  };

  const handleEventChange = async () => {
    const data = await getEventService(account.account_uuid);
    if (data !== undefined) {
      setEvent(data);
      if (data?.event_name === "calling" && data?.event_status === false) {
        const audioElement = document.getElementById("player");
        audioElement.muted = true;
        audioElement.muted = false;
        audioElement.play();
      }
    }
  };

  const handleEventUpdate = async () => {
    await updateEventService(account.account_uuid);
    await localStorage.setItem("system", 5);
    await window.open(`/`);
  };

  useEffect(() => {
    if (account?.account_uuid) {
      handleEventChange();
    }
    setInterval(() => {
      if (account?.account_uuid) {
        handleEventChange();
      }
    }, 5000);
  }, [account]);

  const handleAccountChange = (value) => {
    dispatch(getAccount(value));
  };

  const handleAccountLimitChange = (value) => {
    dispatch(getAccountLimit(value));
  };

  const handleSignIn = async () => {
    if (localStorage.getItem("accountType") === "1") {
      const data = await signInWithEmail(email, password);
      if (data.user !== null) {
        await getAccountService(data.user.id).then((data) => {
          handleAccountChange(data);
          getSiteService(data.account_uuid).then((data) => {
            dispatch(setSiteAccount(data));
            localStorage.setItem("site", JSON.stringify(data[0]["site_uuid"]));
            getAccountLimitService(data[0]["account_uuid"]).then((data) => {
              handleAccountLimitChange(data);
              window.location.reload();
            });
          });
        });

        setIsAuth(true);
      } else {
        messageApi.open({
          type: "error",
          content: "รหัสผ่านหรือรหัสผ่านไม่ถูกต้อง",
        });
      }
    } else if (localStorage.getItem("accountType") === "0") {
      const data = await signInWithUsername(email, password);
      if (data.length > 0) {
        localStorage.setItem("account", JSON.stringify(data[0]));
        localStorage.setItem("site", JSON.stringify(data[0]["site_uuid"]));
        setIsAuth(true);
        window.location.reload();
      } else {
        messageApi.open({
          type: "error",
          content: "รหัสผ่านหรือรหัสผ่านไม่ถูกต้อง",
        });
      }
    } else {
      messageApi.open({
        type: "error",
        content: "กรุณาเลือกประเภทบัญชี",
      });
    }
  };

  useEffect(() => {
    if (
      localStorage.getItem("account") !== null &&
      localStorage.getItem("accountType") === "1"
    ) {
      getAccountService(
        JSON.parse(localStorage.getItem("account"))["account_uuid"]
      ).then((data) => {
        handleAccountChange(data);
        getSiteService(data.account_uuid).then((data) => {
          dispatch(setSiteAccount(data));
        });
      });
      getAccountLimitService(
        JSON.parse(localStorage.getItem("account"))["account_uuid"]
      ).then((data) => {
        handleAccountLimitChange(data);
      });
      setIsAuth(true);
    } else if (
      localStorage.getItem("account") !== null &&
      localStorage.getItem("accountType") === "0"
    ) {
      const data = JSON.parse(localStorage.getItem("account"));
      handleAccountChange(data);
      dispatch(
        setSiteAccount([
          {
            site_name: data["site_name"],
            site_uuid: data["site_uuid"],
          },
        ])
      );
      setIsAuth(true);
    } else {
      setIsAuth(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("system")) {
      localStorage.setItem("system", 2);
    }
  }, []);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  return (
    <Router>
      {contextHolder}
      <Modal
        id="player-modal"
        open={event?.event_name === "calling" && event?.event_status === false}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        closable={false}
        styles={modalStyles}
        className="modern-modal"
        width={isMobile ? '95%' : 520}
      >
        <div className="text-center space-y-3 md:space-y-4 fade-in">
          <div className={`${isMobile ? 'text-base' : 'text-xl'} font-semibold text-gray-800`}>
            🔔 มีการเรียกติดต่อ
          </div>
          <div className={`${isMobile ? 'text-sm' : 'text-lg'} text-gray-600`}>
            จากหน่วยงาน: <span className="font-medium text-blue-600">{event?.event_site}</span>
          </div>
          <div className={`flex justify-center items-center ${isMobile ? 'p-2' : 'p-4'}`}>
            <img 
              className="max-w-full h-auto rounded-lg shadow-lg" 
              style={{ maxWidth: isMobile ? "250px" : "400px" }} 
              src="calling.gif" 
              alt="Incoming call animation"
            />
          </div>

          <audio
            id="player"
            controls
            playsInline
            autoPlay
            style={{ display: "none" }}
          >
            <source
              src="https://woeexpeqouhtojnuyueg.supabase.co/storage/v1/object/public/siamai/temp/calling.mp3"
              type="audio/mp3"
            />
          </audio>

          <Button
            className={`gradient-button w-full ${isMobile ? 'py-2 text-sm' : 'py-3 text-lg'} font-semibold`}
            style={{
              height: isMobile ? "40px" : "50px",
              background: "linear-gradient(45deg, #ff6b6b, #f06595)",
              border: "none",
              borderRadius: isMobile ? "8px" : "12px",
            }}
            type="primary"
            onClick={handleEventUpdate}
          >
            📞 รับสาย
          </Button>
        </div>
      </Modal>
      <Modal
        title={
          <div className="text-center">
            <div className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              🔐 เข้าสู่ระบบบริหารจัดการ
            </div>
          </div>
        }
        closeIcon={false}
        okText="เข้าสู่ระบบ"
        cancelText="ยกเลิก"
        open={!isAuth}
        onOk={handleSignIn}
        styles={modalStyles}
        className="modern-modal"
        width={isMobile ? '95%' : 520}
        okButtonProps={{
          className: "gradient-button",
          style: {
            background: "linear-gradient(45deg, #667eea, #764ba2)",
            border: "none",
            borderRadius: "8px",
            height: isMobile ? "36px" : "40px",
            fontWeight: "600",
            fontSize: isMobile ? "14px" : "16px",
          }
        }}
        cancelButtonProps={{
          style: {
            borderRadius: "8px",
            height: isMobile ? "36px" : "40px",
            fontWeight: "500",
            fontSize: isMobile ? "14px" : "16px",
          }
        }}
      >
        <div className="fade-in space-y-4 md:space-y-6">
          <p className="text-center text-gray-600 text-base md:text-lg">
            กรุณาเข้าสู่ระบบเพื่อใช้งาน
          </p>
          
          <div className="space-y-3 md:space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                เลือกประเภทบัญชี
              </label>
              <Select
                className="w-full"
                size={isMobile ? "middle" : "large"}
                defaultValue="0"
                onSelect={(value) => {
                  localStorage.setItem("accountType", value);
                  setAccountType(value);
                }}
                onChange={(value) => {
                  localStorage.setItem("accountType", value);
                  setAccountType(value);
                }}
                options={[
                  { value: "0", label: isMobile ? "🏢 หน่วยงาน" : "🏢 สำหรับหน่วยงาน" },
                  { value: "1", label: isMobile ? "🏬 บริษัท" : "🏬 สำหรับบริษัท" },
                ]}
                style={{
                  borderRadius: "8px",
                }}
              />
            </div>
            
            <div className="space-y-3">
              <Input
                placeholder={isMobile ? "👤 ผู้ใช้" : "👤 ชื่อผู้ใช้"}
                size={isMobile ? "middle" : "large"}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                style={{
                  borderRadius: "8px",
                  height: isMobile ? "40px" : "48px",
                }}
                className="transition-all duration-200 focus:shadow-lg"
              />
              <Input.Password
                size={isMobile ? "middle" : "large"}
                placeholder={isMobile ? "🔒 รหัสผ่าน" : "🔒 รหัสผ่าน"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                }}
                style={{
                  borderRadius: "8px",
                  height: isMobile ? "40px" : "48px",
                }}
                className="transition-all duration-200 focus:shadow-lg"
              />
            </div>
          </div>
        </div>
      </Modal>
      <Layout 
        style={{ 
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)"
        }}
        className="app-layout"
      >
        <Sidebar collapsed={collapsed} />
        <Layout 
          className="fade-in" 
          style={{ 
            marginLeft: isMobile ? 0 : undefined,
            minHeight: '100vh'
          }}
        >
          <Header
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            colorBgContainer={colorBgContainer}
            isMobile={isMobile}
          />

          <Routes>
            {selectedSystem === 1 && (
              <Route
                path="/"
                element={
                  <DashboardVMSPage
                    colorBgContainer={colorBgContainer}
                    borderRadiusLG={borderRadiusLG}
                  />
                }
              />
            )}

            {selectedSystem === 2 && (
              <Route
                path="/"
                element={
                  <DashboardPatrolPage
                    colorBgContainer={colorBgContainer}
                    borderRadiusLG={borderRadiusLG}
                  />
                }
              />
            )}

            {selectedSystem === 3 && (
              <Route
                path="/"
                element={
                  <DashboardTimeAttendancePage
                    colorBgContainer={colorBgContainer}
                    borderRadiusLG={borderRadiusLG}
                  />
                }
              />
            )}

            {selectedSystem === 4 && (
              <Route
                path="/"
                element={
                  <DashboardDailyPage
                    colorBgContainer={colorBgContainer}
                    borderRadiusLG={borderRadiusLG}
                  />
                }
              />
            )}

            {selectedSystem === 5 && (
              <Route
                path="/"
                element={
                  <DashboardCentralizePage
                    colorBgContainer={colorBgContainer}
                    borderRadiusLG={borderRadiusLG}
                  />
                }
              />
            )}

            {selectedSystem === 6 && (
              <Route
                path="/"
                element={
                  <DashboardIOTPage
                    colorBgContainer={colorBgContainer}
                    borderRadiusLG={borderRadiusLG}
                  />
                }
              />
            )}

            <Route
              path="/docs"
              element={
                <IndexDocsPage
                  account={account}
                  colorBgContainer={colorBgContainer}
                  borderRadiusLG={borderRadiusLG}
                />
              }
            />

            <Route
              path="/docs/employee"
              element={
                <EmployeePage
                  colorBgContainer={colorBgContainer}
                  borderRadiusLG={borderRadiusLG}
                  site_uuid={selectedSite}
                />
              }
            />

            <Route
              path="/docs/stock"
              element={
                <StockPage
                  colorBgContainer={colorBgContainer}
                  borderRadiusLG={borderRadiusLG}
                  site_uuid={selectedSite}
                />
              }
            />

            <Route
              path="/docs/contract"
              element={
                <ContractPage
                  colorBgContainer={colorBgContainer}
                  borderRadiusLG={borderRadiusLG}
                  site_uuid={selectedSite}
                />
              }
            />

            <Route
              path="/docs/sow"
              element={
                <SOWPage
                  colorBgContainer={colorBgContainer}
                  borderRadiusLG={borderRadiusLG}
                  site_uuid={selectedSite}
                />
              }
            />

            <Route
              path="/docs/training"
              element={
                <TrainingPage
                  colorBgContainer={colorBgContainer}
                  borderRadiusLG={borderRadiusLG}
                  site_uuid={selectedSite}
                />
              }
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage
                    colorBgContainer={colorBgContainer}
                    borderRadiusLG={borderRadiusLG}
                  />
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProfilePage
                  account={account}
                  colorBgContainer={colorBgContainer}
                  borderRadiusLG={borderRadiusLG}
                />
              }
            />

            {localStorage.getItem("accountType") === "1" && (
              <>
                <Route
                  path="/system/sites"
                  element={
                    <SitePage
                      account={account}
                      accountLimit={accountLimit}
                      colorBgContainer={colorBgContainer}
                      borderRadiusLG={borderRadiusLG}
                    />
                  }
                />
                <Route
                  path="/system/program"
                  element={
                    selectedSystem === 1 ? (
                      <ProgramVMSPage
                        system={systemName[selectedSystem]}
                        site_uuid={selectedSite}
                        colorBgContainer={colorBgContainer}
                        borderRadiusLG={borderRadiusLG}
                      />
                    ) : (
                      <ProgramPatrolPage
                        accountLimit={accountLimit}
                        site_uuid={selectedSite}
                        colorBgContainer={colorBgContainer}
                        borderRadiusLG={borderRadiusLG}
                      />
                    )
                  }
                />
                <Route
                  path="/system/cycle"
                  element={
                    selectedSystem === 2 && (
                      <CyclePatrolPage
                        system={systemName[selectedSystem]}
                        site_uuid={selectedSite}
                        colorBgContainer={colorBgContainer}
                        borderRadiusLG={borderRadiusLG}
                      />
                    )
                  }
                />
                <Route
                  path="/system/subcontract"
                  element={
                    <SubContractVMSPage
                      system={systemName[selectedSystem]}
                      site_uuid={selectedSite}
                      accountLimit={accountLimit}
                      colorBgContainer={colorBgContainer}
                      borderRadiusLG={borderRadiusLG}
                    />
                  }
                />
                <Route
                  path="/system/devices"
                  element={
                    <DeviceVMSPage
                      system={systemName[selectedSystem]}
                      site_uuid={selectedSite}
                      accountLimit={accountLimit}
                      colorBgContainer={colorBgContainer}
                      borderRadiusLG={borderRadiusLG}
                    />
                  }
                />
                <Route
                  path="/system/device-noti"
                  element={
                    <DeviceNotiPage
                      system={systemName[selectedSystem]}
                      site_uuid={selectedSite}
                      accountLimit={accountLimit}
                      colorBgContainer={colorBgContainer}
                      borderRadiusLG={borderRadiusLG}
                    />
                  }
                />
                <Route
                  path="/system/notify"
                  element={
                    <NotifyPage
                      system={systemName[selectedSystem]}
                      site_uuid={selectedSite}
                      colorBgContainer={colorBgContainer}
                      borderRadiusLG={borderRadiusLG}
                    />
                  }
                />
              </>
            )}
          </Routes>
          <Footer />
        </Layout>
        {isMobile && <MobileBottomNav />}
      </Layout>
    </Router>
  );
};

export default App;
